import React, { useState, FormEvent, useEffect } from 'react';
import { ref, get, set, push, remove, onValue, off, serverTimestamp } from 'firebase/database';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword, signOut, onAuthStateChanged, User } from 'firebase/auth';
import { db, auth } from '../../firebase/config';
import './Admin.css';

interface Testimonial {
  id?: string;
  rating: number;
  text: string;
  author: string;
  title: string;
  initials: string;
  createdAt?: number;
}

interface Visit {
  id?: string;
  page: string;
  timestamp: number;
  userAgent?: string;
}

type TabType = 'testimonials' | 'statistics';

const Admin: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [isCheckingAdmins, setIsCheckingAdmins] = useState(true);
  const [showRegister, setShowRegister] = useState(false);

  // Auth form states
  const [authFormData, setAuthFormData] = useState({
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [authError, setAuthError] = useState('');
  const [isAuthLoading, setIsAuthLoading] = useState(false);

  const [activeTab, setActiveTab] = useState<TabType>('testimonials');
  const [formData, setFormData] = useState<Testimonial>({
    rating: 5,
    text: '',
    author: '',
    title: '',
    initials: ''
  });

  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');
  const [errorDetails, setErrorDetails] = useState('');

  // İstatistikler
  const [visits, setVisits] = useState<Visit[]>([]);
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  const [stats, setStats] = useState({
    totalVisits: 0,
    todayVisits: 0,
    thisWeekVisits: 0,
    thisMonthVisits: 0,
    pageViews: {} as Record<string, number>
  });

  // Admin kontrolü - kaç admin var?
  useEffect(() => {
    const checkAdmins = async () => {
      try {
        const adminsRef = ref(db, 'admins');
        const snapshot = await get(adminsRef);
        
        if (snapshot.exists()) {
          const data = snapshot.val();
          const adminCount = Object.keys(data).length;
          setShowRegister(adminCount === 0);
        } else {
          setShowRegister(true);
        }
      } catch (error) {
        console.error('Admin kontrolü hatası:', error);
        setShowRegister(true);
      } finally {
        setIsCheckingAdmins(false);
      }
    };

    checkAdmins();
  }, []);

  // Auth state kontrolü
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setIsCheckingAuth(false);
    });

    return () => unsubscribe();
  }, []);

  // Yorumları Firebase'den çek (Realtime)
  const loadTestimonials = () => {
    setIsLoading(true);
    setMessage('');
    setErrorDetails('');
    
    const testimonialsRef = ref(db, 'testimonials');
    
    const unsubscribe = onValue(testimonialsRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        const loadedTestimonials: Testimonial[] = [];
        
        Object.keys(data).forEach((key) => {
          loadedTestimonials.push({
            id: key,
            ...data[key]
          } as Testimonial);
        });
        
        // Tarihe göre sırala (en yeni önce)
        loadedTestimonials.sort((a, b) => {
          const timeA = a.createdAt || 0;
          const timeB = b.createdAt || 0;
          return timeB - timeA;
        });
        
        setTestimonials(loadedTestimonials);
      } else {
        setTestimonials([]);
      }
      setIsLoading(false);
    }, (error) => {
      console.error('Yorumlar yüklenirken hata:', error);
      setMessage('Yorumlar yüklenirken bir hata oluştu.');
      setErrorDetails(error.message || 'Bilinmeyen hata');
      setIsLoading(false);
    });

    return () => {
      off(testimonialsRef);
    };
  };

  // İstatistikleri yükle
  const loadStatistics = async () => {
    setIsLoadingStats(true);
    try {
      const visitsRef = ref(db, 'visits');
      const snapshot = await get(visitsRef);
      
      const loadedVisits: Visit[] = [];
      
      if (snapshot.exists()) {
        const data = snapshot.val();
        Object.keys(data).forEach((key) => {
          loadedVisits.push({
            id: key,
            ...data[key]
          } as Visit);
        });
        
        // Tarihe göre sırala (en yeni önce)
        loadedVisits.sort((a, b) => {
          const timeA = a.timestamp || 0;
          const timeB = b.timestamp || 0;
          return timeB - timeA;
        });
      }
      
      setVisits(loadedVisits);

      // İstatistikleri hesapla
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const thisWeek = new Date(today);
      thisWeek.setDate(today.getDate() - 7);
      const thisMonth = new Date(today);
      thisMonth.setMonth(today.getMonth() - 1);

      const pageViews: Record<string, number> = {};
      let todayCount = 0;
      let weekCount = 0;
      let monthCount = 0;

      loadedVisits.forEach((visit) => {
        // Sayfa görüntülemeleri
        const page = visit.page || 'unknown';
        pageViews[page] = (pageViews[page] || 0) + 1;

        // Tarih kontrolü
        if (visit.timestamp) {
          const visitDate = new Date(visit.timestamp);
          if (visitDate >= today) todayCount++;
          if (visitDate >= thisWeek) weekCount++;
          if (visitDate >= thisMonth) monthCount++;
        }
      });

      setStats({
        totalVisits: loadedVisits.length,
        todayVisits: todayCount,
        thisWeekVisits: weekCount,
        thisMonthVisits: monthCount,
        pageViews
      });
    } catch (error: any) {
      console.error('İstatistikler yüklenirken hata:', error);
    } finally {
      setIsLoadingStats(false);
    }
  };

  // Sekme değiştiğinde ilgili verileri yükle
  useEffect(() => {
    if (user && activeTab === 'testimonials') {
      const unsubscribe = loadTestimonials();
      return () => {
        if (unsubscribe) unsubscribe();
      };
    } else if (user && activeTab === 'statistics') {
      loadStatistics();
    }
  }, [activeTab, user]);

  // Kayıt ol
  const handleRegister = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsAuthLoading(true);
    setAuthError('');

    if (authFormData.password !== authFormData.confirmPassword) {
      setAuthError('Şifreler eşleşmiyor!');
      setIsAuthLoading(false);
      return;
    }

    if (authFormData.password.length < 6) {
      setAuthError('Şifre en az 6 karakter olmalıdır!');
      setIsAuthLoading(false);
      return;
    }

    try {
      // Firebase Auth'da kullanıcı oluştur
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        authFormData.email,
        authFormData.password
      );

      // Admin node'una ekle
      const adminRef = ref(db, `admins/${userCredential.user.uid}`);
      await set(adminRef, {
        uid: userCredential.user.uid,
        email: authFormData.email,
        createdAt: serverTimestamp()
      });

      setUser(userCredential.user);
      setShowRegister(false);
    } catch (error: any) {
      console.error('Kayıt hatası:', error);
      setAuthError(error.message || 'Kayıt olurken bir hata oluştu.');
    } finally {
      setIsAuthLoading(false);
    }
  };

  // Giriş yap
  const handleLogin = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsAuthLoading(true);
    setAuthError('');

    try {
      // Admin kontrolü - bu email admin mi?
      const adminsRef = ref(db, 'admins');
      const snapshot = await get(adminsRef);
      
      let isAdmin = false;
      if (snapshot.exists()) {
        const data = snapshot.val();
        Object.values(data).forEach((admin: any) => {
          if (admin.email === authFormData.email) {
            isAdmin = true;
          }
        });
      }

      if (!isAdmin) {
        setAuthError('Bu email ile admin hesabı bulunamadı!');
        setIsAuthLoading(false);
        return;
      }

      // Firebase Auth ile giriş yap
      await signInWithEmailAndPassword(
        auth,
        authFormData.email,
        authFormData.password
      );

      // User state onAuthStateChanged tarafından güncellenecek
    } catch (error: any) {
      console.error('Giriş hatası:', error);
      if (error.code === 'auth/wrong-password') {
        setAuthError('Şifre hatalı!');
      } else if (error.code === 'auth/user-not-found') {
        setAuthError('Bu email ile kullanıcı bulunamadı!');
      } else {
        setAuthError(error.message || 'Giriş yaparken bir hata oluştu.');
      }
    } finally {
      setIsAuthLoading(false);
    }
  };

  // Çıkış yap
  const handleLogout = async () => {
    try {
      await signOut(auth);
      setUser(null);
      setAuthFormData({ email: '', password: '', confirmPassword: '' });
    } catch (error) {
      console.error('Çıkış hatası:', error);
    }
  };

  const handleAuthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setAuthFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'rating' ? parseInt(value) : value
    }));

    // İnitials otomatik oluştur
    if (name === 'author') {
      const names = value.trim().split(' ');
      if (names.length >= 2) {
        const initials = (names[0][0] + names[names.length - 1][0]).toUpperCase();
        setFormData(prev => ({ ...prev, initials }));
      } else if (names.length === 1 && names[0].length > 0) {
        const initials = names[0][0].toUpperCase();
        setFormData(prev => ({ ...prev, initials }));
      }
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    setMessage('');
    setErrorDetails('');

    // İnitials yoksa oluştur
    if (!formData.initials && formData.author) {
      const names = formData.author.trim().split(' ');
      if (names.length >= 2) {
        formData.initials = (names[0][0] + names[names.length - 1][0]).toUpperCase();
      } else if (names.length === 1) {
        formData.initials = names[0][0].toUpperCase();
      }
    }

    try {
      const testimonialsRef = ref(db, 'testimonials');
      const newTestimonialRef = push(testimonialsRef);
      
      await set(newTestimonialRef, {
        rating: formData.rating,
        text: formData.text.trim(),
        author: formData.author.trim(),
        title: formData.title.trim(),
        initials: formData.initials.trim() || '??',
        createdAt: serverTimestamp()
      });
      
      setSubmitStatus('success');
      setMessage('Yorum başarıyla eklendi!');
      setFormData({
        rating: 5,
        text: '',
        author: '',
        title: '',
        initials: ''
      });
    } catch (error: any) {
      console.error('Yorum eklenirken hata:', error);
      setSubmitStatus('error');
      setMessage('Yorum eklenirken bir hata oluştu.');
      setErrorDetails(error.message || 'Bilinmeyen hata. Firebase Realtime Database kurallarını kontrol edin.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Bu yorumu silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      const testimonialRef = ref(db, `testimonials/${id}`);
      await remove(testimonialRef);
      setMessage('Yorum başarıyla silindi!');
    } catch (error: any) {
      console.error('Yorum silinirken hata:', error);
      setMessage('Yorum silinirken bir hata oluştu: ' + (error.message || 'Bilinmeyen hata'));
    }
  };

  // Yükleniyor durumu
  if (isCheckingAuth || isCheckingAdmins) {
    return (
      <div className="admin-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Yükleniyor...</p>
        </div>
      </div>
    );
  }

  // Giriş yapılmamışsa veya kayıt gerekliyse
  if (!user) {
    return (
      <div className="admin-page">
        <section className="admin-hero">
          <div className="container">
            <h1 className="page-title">Admin Paneli</h1>
            <p className="page-subtitle">
              {showRegister ? 'İlk admin hesabını oluşturun' : 'Giriş yapın'}
            </p>
          </div>
        </section>

        <section className="admin-content section">
          <div className="container">
            <div className="auth-container">
              <div className="auth-form-wrapper">
                <h2 className="admin-section-title">
                  {showRegister ? 'Admin Kaydı' : 'Admin Girişi'}
                </h2>
                
                {authError && (
                  <div className="form-message error">
                    {authError}
                  </div>
                )}

                {showRegister ? (
                  <form className="admin-form" onSubmit={handleRegister}>
                    <div className="form-group">
                      <label htmlFor="email">E-posta *</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={authFormData.email}
                        onChange={handleAuthChange}
                        required
                        placeholder="admin@example.com"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="password">Şifre *</label>
                      <input
                        type="password"
                        id="password"
                        name="password"
                        value={authFormData.password}
                        onChange={handleAuthChange}
                        required
                        placeholder="En az 6 karakter"
                        minLength={6}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="confirmPassword">Şifre Tekrar *</label>
                      <input
                        type="password"
                        id="confirmPassword"
                        name="confirmPassword"
                        value={authFormData.confirmPassword}
                        onChange={handleAuthChange}
                        required
                        placeholder="Şifrenizi tekrar girin"
                        minLength={6}
                      />
                    </div>

                    <button
                      type="submit"
                      className="btn btn-primary form-submit-btn"
                      disabled={isAuthLoading}
                    >
                      {isAuthLoading ? 'Kaydediliyor...' : 'Kayıt Ol'}
                    </button>
                  </form>
                ) : (
                  <form className="admin-form" onSubmit={handleLogin}>
                    <div className="form-group">
                      <label htmlFor="email">E-posta *</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={authFormData.email}
                        onChange={handleAuthChange}
                        required
                        placeholder="admin@example.com"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="password">Şifre *</label>
                      <input
                        type="password"
                        id="password"
                        name="password"
                        value={authFormData.password}
                        onChange={handleAuthChange}
                        required
                        placeholder="Şifrenizi girin"
                      />
                    </div>

                    <button
                      type="submit"
                      className="btn btn-primary form-submit-btn"
                      disabled={isAuthLoading}
                    >
                      {isAuthLoading ? 'Giriş yapılıyor...' : 'Giriş Yap'}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  // Giriş yapılmışsa admin paneli göster
  return (
    <div className="admin-page">
      <section className="admin-hero">
        <div className="container">
          <div className="admin-hero-content">
            <div>
              <h1 className="page-title">Admin Paneli</h1>
              <p className="page-subtitle">
                Site yönetimi ve istatistikler
              </p>
            </div>
            <div className="admin-user-info">
              <span>{user.email}</span>
              <button className="btn btn-secondary btn-logout" onClick={handleLogout}>
                Çıkış Yap
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="admin-content section">
        <div className="container">
          {/* Sekmeler */}
          <div className="admin-tabs">
            <button
              className={`admin-tab ${activeTab === 'testimonials' ? 'active' : ''}`}
              onClick={() => setActiveTab('testimonials')}
            >
              Yorum Yönetimi
            </button>
            <button
              className={`admin-tab ${activeTab === 'statistics' ? 'active' : ''}`}
              onClick={() => setActiveTab('statistics')}
            >
              Site İstatistikleri
            </button>
          </div>

          {/* Yorum Yönetimi Sekmesi */}
          {activeTab === 'testimonials' && (
            <div className="admin-tab-content">
              <div className="admin-grid">
                {/* Yorum Ekleme Formu */}
                <div className="admin-form-wrapper">
                  <h2 className="admin-section-title">Yeni Yorum Ekle</h2>
                  <form className="admin-form" onSubmit={handleSubmit}>
                    <div className="form-group">
                      <label htmlFor="author">Ad Soyad *</label>
                      <input
                        type="text"
                        id="author"
                        name="author"
                        value={formData.author}
                        onChange={handleChange}
                        required
                        placeholder="Örn: Mehmet Aksoy"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="title">Başlık/Proje *</label>
                      <input
                        type="text"
                        id="title"
                        name="title"
                        value={formData.title}
                        onChange={handleChange}
                        required
                        placeholder="Örn: Villa Projesi, İstanbul"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="rating">Puan *</label>
                      <select
                        id="rating"
                        name="rating"
                        value={formData.rating}
                        onChange={handleChange}
                        required
                      >
                        <option value="5">5 Yıldız</option>
                        <option value="4">4 Yıldız</option>
                        <option value="3">3 Yıldız</option>
                        <option value="2">2 Yıldız</option>
                        <option value="1">1 Yıldız</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label htmlFor="text">Yorum Metni *</label>
                      <textarea
                        id="text"
                        name="text"
                        value={formData.text}
                        onChange={handleChange}
                        required
                        rows={6}
                        placeholder="Müşteri yorumunu buraya yazın..."
                      ></textarea>
                    </div>

                    <div className="form-group">
                      <label htmlFor="initials">Baş Harfler</label>
                      <input
                        type="text"
                        id="initials"
                        name="initials"
                        value={formData.initials}
                        onChange={handleChange}
                        maxLength={2}
                        placeholder="Otomatik oluşturulur"
                      />
                      <small>Ad soyad girildiğinde otomatik oluşturulur</small>
                    </div>

                    {message && (
                      <div className={`form-message ${submitStatus === 'success' ? 'success' : submitStatus === 'error' ? 'error' : 'info'}`}>
                        {message}
                        {errorDetails && (
                          <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', opacity: 0.8 }}>
                            {errorDetails}
                          </div>
                        )}
                      </div>
                    )}

                    <button
                      type="submit"
                      className="btn btn-primary form-submit-btn"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Ekleniyor...' : 'Yorum Ekle'}
                    </button>
                  </form>
                </div>

                {/* Mevcut Yorumlar Listesi */}
                <div className="admin-list-wrapper">
                  <div className="admin-list-header">
                    <h2 className="admin-section-title">Mevcut Yorumlar</h2>
                    <button 
                      className="btn btn-secondary btn-refresh"
                      onClick={() => loadTestimonials()}
                      disabled={isLoading}
                    >
                      {isLoading ? 'Yükleniyor...' : 'Yenile'}
                    </button>
                  </div>

                  {isLoading && testimonials.length === 0 ? (
                    <div className="loading-message">Yorumlar yükleniyor...</div>
                  ) : testimonials.length === 0 ? (
                    <div className="empty-message">Henüz yorum eklenmemiş.</div>
                  ) : (
                    <div className="testimonials-list">
                      {testimonials.map((testimonial) => (
                        <div key={testimonial.id} className="testimonial-item">
                          <div className="testimonial-item-header">
                            <div className="testimonial-item-author">
                              <div className="testimonial-item-avatar">
                                {testimonial.initials || '??'}
                              </div>
                              <div>
                                <h4>{testimonial.author}</h4>
                                <p>{testimonial.title}</p>
                              </div>
                            </div>
                            <div className="testimonial-item-rating">
                              {[...Array(testimonial.rating)].map((_, i) => (
                                <span key={i} className="star">★</span>
                              ))}
                            </div>
                          </div>
                          <p className="testimonial-item-text">{testimonial.text}</p>
                          <button
                            className="btn btn-danger btn-delete"
                            onClick={() => testimonial.id && handleDelete(testimonial.id)}
                          >
                            Sil
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Site İstatistikleri Sekmesi */}
          {activeTab === 'statistics' && (
            <div className="admin-tab-content">
              <div className="stats-container">
                <div className="stats-header">
                  <h2 className="admin-section-title">Site İstatistikleri</h2>
                  <button 
                    className="btn btn-secondary btn-refresh"
                    onClick={loadStatistics}
                    disabled={isLoadingStats}
                  >
                    {isLoadingStats ? 'Yükleniyor...' : 'Yenile'}
                  </button>
                </div>

                {isLoadingStats ? (
                  <div className="loading-message">İstatistikler yükleniyor...</div>
                ) : (
                  <>
                    {/* İstatistik Kartları */}
                    <div className="stats-cards">
                      <div className="stat-card">
                        <div className="stat-icon">👥</div>
                        <div className="stat-content">
                          <h3 className="stat-value">{stats.totalVisits}</h3>
                          <p className="stat-label">Toplam Ziyaret</p>
                        </div>
                      </div>
                      <div className="stat-card">
                        <div className="stat-icon">📅</div>
                        <div className="stat-content">
                          <h3 className="stat-value">{stats.todayVisits}</h3>
                          <p className="stat-label">Bugün</p>
                        </div>
                      </div>
                      <div className="stat-card">
                        <div className="stat-icon">📊</div>
                        <div className="stat-content">
                          <h3 className="stat-value">{stats.thisWeekVisits}</h3>
                          <p className="stat-label">Bu Hafta</p>
                        </div>
                      </div>
                      <div className="stat-card">
                        <div className="stat-icon">📈</div>
                        <div className="stat-content">
                          <h3 className="stat-value">{stats.thisMonthVisits}</h3>
                          <p className="stat-label">Bu Ay</p>
                        </div>
                      </div>
                    </div>

                    {/* Sayfa Görüntülemeleri */}
                    <div className="page-views-section">
                      <h3 className="section-subtitle">Sayfa Görüntülemeleri</h3>
                      <div className="page-views-list">
                        {Object.keys(stats.pageViews).length === 0 ? (
                          <div className="empty-message">Henüz sayfa görüntüleme verisi yok.</div>
                        ) : (
                          Object.entries(stats.pageViews)
                            .sort(([, a], [, b]) => b - a)
                            .map(([page, count]) => (
                              <div key={page} className="page-view-item">
                                <span className="page-name">{page === '/' ? 'Ana Sayfa' : page}</span>
                                <span className="page-count">{count} görüntüleme</span>
                              </div>
                            ))
                        )}
                      </div>
                    </div>

                    {/* Son Ziyaretler */}
                    <div className="recent-visits-section">
                      <h3 className="section-subtitle">Son Ziyaretler</h3>
                      <div className="visits-list">
                        {visits.length === 0 ? (
                          <div className="empty-message">Henüz ziyaret kaydı yok.</div>
                        ) : (
                          <div className="visits-table">
                            <div className="visits-table-header">
                              <div>Sayfa</div>
                              <div>Tarih</div>
                            </div>
                            {visits.slice(0, 20).map((visit) => (
                              <div key={visit.id} className="visits-table-row">
                                <div>{visit.page === '/' ? 'Ana Sayfa' : visit.page}</div>
                                <div>
                                  {visit.timestamp 
                                    ? new Date(visit.timestamp).toLocaleString('tr-TR')
                                    : 'Bilinmiyor'}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Admin;
